// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @fullcalendar components
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";

// @mui material components
import Card from "@mui/material/Card";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

// Custom styles for Calendar
import CalendarRoot from "cxfcomponents/Calendar/CalendarRoot";
import { useEffect, useState } from "react";

const calendarDefaultProps = {
  header: {
    title: "",
    date: "",
  },
};

const repeatTypeToSplit = ["daily", "weekly", "monthly"];

function Calendar({
  header = calendarDefaultProps.header,
  combinedEvents,
  setCombinedEvents,
  events,
  ...rest
}) {
  const validStatuses = [
    "active",
    "recurring",
    "executing",
    "canceling",
    "completed",
    "failed",
    "paused",
  ];
  const [calendarData, setCalendarData] = useState({});
  const [calendarEvents, setCalendarEvents] = useState([]);

  const mixEventClassname = (event) => {
    let className;

    if (validStatuses.find((item) => item === event.className?.toLowerCase())) {
      className = `event-${event.className.toLowerCase()}`;
    } else {
      className = "event-info";
    }

    if (event.repeat !== "not_repeat") {
      className = "event-recurring";
      if (event.status === "Paused") {
        className = "event-paused";
      }
    }

    return {
      ...event,
      className,
    };
  };

  useEffect(() => {
    if (!calendarData.start) return;

    const notRecurringEvents = [];
    const recurringEvents = [];

    events.forEach((event) => {
      if (!repeatTypeToSplit.includes(event.repeat)) {
        notRecurringEvents.push(mixEventClassname(event));
      } else {
        recurringEvents.push(mixEventClassname(event));
      }
    });

    let combinedEvents = [...notRecurringEvents];

    recurringEvents.forEach((event) => {
      const startDate = new Date(event.start);
      const endDate = event.end ? new Date(event.end) : new Date(calendarData.end);
      const days = event.repetition.split(",").map((day) => parseInt(day));
      const newRecurringEvents = [];
      const endDatePlusOne = new Date(endDate);
      endDatePlusOne.setDate(endDate.getDate() + 1);

      switch (event.repeat) {
        case "daily":
          for (
            let currentDate =
              startDate > new Date(calendarData.start) ? startDate : new Date(calendarData.start);
            currentDate <= endDatePlusOne;
            currentDate.setDate(currentDate.getDate() + 1)
          ) {
            newRecurringEvents.push({
              ...event,
              start: new Date(currentDate).toISOString(),
              end: null,
              savedEndDate: event.end,
              savedStartDate: event.start,
              eventId: event.id,
            });
          }
          break;
        case "weekly":
          for (
            let currentDate =
              startDate > new Date(calendarData.start) ? startDate : new Date(calendarData.start);
            currentDate < endDatePlusOne;
            currentDate.setDate(currentDate.getDate() + 1)
          ) {
            const dayOfWeek = currentDate.getDay();

            if (days.includes(dayOfWeek)) {
              newRecurringEvents.push({
                ...event,
                start: new Date(currentDate).toISOString(),
                end: null,
                savedEndDate: event.end,
                savedStartDate: event.start,
                eventId: event.id,
              });
            }
          }

          break;
        case "monthly":
          for (
            let currentDate =
              startDate > new Date(calendarData.start) ? startDate : new Date(calendarData.start);
            currentDate < endDatePlusOne;
            currentDate.setDate(currentDate.getDate() + 1)
          ) {
            const monthDate = parseInt(event.repetition);

            if (currentDate.getDate() === monthDate) {
              newRecurringEvents.push({
                ...event,
                start: new Date(currentDate).toISOString(),
                end: null,
                savedEndDate: event.end,
                savedStartDate: event.start,
                eventId: event.id,
              });
            }
          }
      }

      combinedEvents = combinedEvents.concat(newRecurringEvents);
    });

    setCombinedEvents(combinedEvents);
  }, [calendarData, events]);

  useEffect(() => {
    setCalendarEvents(
      combinedEvents.map((event) => {
        if (event.status === "Recurring") {
          const { id, ...restEvent } = event;

          return restEvent;
        }

        return event;
      })
    );
  }, [combinedEvents]);

  return (
    <Card sx={{ height: "100%", boxShadow: "none" }}>
      <SoftBox pt={2} px={2} lineHeight={1}>
        {header.title && (
          <SoftTypography variant="h6" fontWeight="medium" textTransform="capitalize">
            {header.title}
          </SoftTypography>
        )}
        {header.date && (
          <SoftTypography component="p" variant="button" color="text" fontWeight="medium">
            {header.date}
          </SoftTypography>
        )}
      </SoftBox>
      <CalendarRoot p={2}>
        <FullCalendar
          {...rest}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          events={calendarEvents}
          height="100%"
          headerToolbar={{
            left: "title",
            right: "prev,next today dayGridMonth,timeGridWeek",
          }}
          datesSet={(e) => setCalendarData(e)}
        />
      </CalendarRoot>
    </Card>
  );
}

// Typechecking props for the Calendar
Calendar.propTypes = {
  header: PropTypes.shape({
    title: PropTypes.string,
    date: PropTypes.string,
  }),
};

export default Calendar;
